import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import {
  useCategories,
  useGenerateQuestions,
} from "@/hooks/use-questions";
import type { GenerateQuestionsResult } from "@/lib/types";
import { createErrorMessage } from "@/lib/error-message";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader2, Sparkles, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";

export const Route = createFileRoute("/_main/exams/$slug/admin/questions/generate")({
  component: GenerateQuestionsPage,
});

function GenerateQuestionsPage() {
  const { slug: examSlug } = Route.useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();

  const [category, setCategory] = useState("");
  const [difficulty, setDifficulty] = useState("중");
  const [count, setCount] = useState(5);
  const [result, setResult] = useState<GenerateQuestionsResult | null>(null);
  const [error, setError] = useState("");
  const { data: categories = [] } = useCategories(examSlug);
  const generateMutation = useGenerateQuestions(examSlug);
  const generating = generateMutation.isPending;

  useEffect(() => {
    if (!authLoading && user?.role !== "admin") {
      navigate({
        to: "/exams/$slug",
        params: { slug: examSlug },
        replace: true,
      });
    }
  }, [authLoading, user, navigate, examSlug]);

  async function handleGenerate() {
    setError("");
    setResult(null);
    try {
      const res = await generateMutation.mutateAsync({ category, difficulty, count });
      setResult(res);
    } catch (err) {
      setError(createErrorMessage(err, "생성에 실패했습니다"));
    }
  }

  if (authLoading || user?.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex items-center gap-3">
          <Link
            to="/exams/$slug/admin/questions"
            params={{ slug: examSlug }}
          >
            <Button variant="ghost" size="sm" className="cursor-pointer gap-1">
              <ArrowLeft className="h-4 w-4" />
              {t("common.back")}
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">{t("admin.generate_title")}</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              {t("admin.generate_settings")}
            </CardTitle>
            <CardDescription>
              {t("admin.generate_desc")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">{t("common.category")}</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              >
                <option value="">{t("admin.generate_all_category")}</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">{t("admin.generate_difficulty")}</label>
              <div className="flex gap-2">
                {["하", "중", "상"].map((d) => (
                  <Button
                    key={d}
                    variant={difficulty === d ? "default" : "outline"}
                    size="sm"
                    className="cursor-pointer"
                    onClick={() => setDifficulty(d)}
                  >
                    {d}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium">{t("admin.generate_count")}</label>
              <Input
                type="number"
                min={1}
                max={20}
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                className="w-32"
              />
              <p className="mt-1 text-xs text-zinc-500">{t("admin.generate_count_placeholder")}</p>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="w-full cursor-pointer"
            >
              {generating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("admin.generate_loading")}
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  {t("admin.generate_button")}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {result && (
          <Card className="mt-4">
            <CardContent className="py-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <span className="font-medium">
                  {result.generated}{t("admin.generate_success")}
                </span>
              </div>
              {result.failed > 0 && (
                <p className="mt-1 text-sm text-amber-600">
                  {result.failed}{t("admin.generate_failed")}
                </p>
              )}
              {result.errors && result.errors.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-zinc-500">
                  {result.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              )}
              <div className="mt-3 flex gap-2">
                <Link
                  to="/exams/$slug/admin/questions/review"
                  params={{ slug: examSlug }}
                >
                  <Button size="sm" className="cursor-pointer">
                    {t("admin.generate_review")}
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  className="cursor-pointer"
                  onClick={() => setResult(null)}
                >
                  {t("admin.generate_more")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card className="mt-4 border-red-200">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                <span className="text-sm">{error}</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
